import { FormEvent, useState } from 'react';

interface Step2Props {
  data: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const COUNTRIES = ['Spain', 'Portugal', 'France', 'Italy', 'Greece', 'UK', 'USA', 'Other'];

const isKnownCountry = (val: string) => COUNTRIES.slice(0, -1).includes(val);

export default function Step2Property({ data, onChange, onNext, onBack }: Step2Props) {
  const [pullFromListing, setPullFromListing] = useState(data.pullFromListing === 'true');
  const [countrySelect, setCountrySelect] = useState<string>(
    data.country && !isKnownCountry(data.country) && data.country !== '' ? 'Other' : (data.country || '')
  );

  const handlePullToggle = () => {
    const next = !pullFromListing;
    setPullFromListing(next);
    onChange('pullFromListing', next ? 'true' : 'false');
    if (!next) onChange('listingUrl', '');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!data.villaName || !data.country || !data.region || !data.bedrooms || !data.maxGuests) return;
    onNext();
  };

  return (
    <div className="w-full max-w-xl">
      <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-5">Step 2 of 6</p>
      <h2 className="font-serif text-4xl md:text-5xl text-[#141413] mb-3 leading-tight">The property.</h2>
      <p className="text-[#77776f] text-sm font-sans leading-relaxed mb-10">
        Everything we need to build your listing and match you with the right guests.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Pull from listing checkbox card */}
        <button
          type="button"
          onClick={handlePullToggle}
          className={`flex items-start gap-4 px-5 py-4 rounded-xl text-left border-2 transition-all duration-200 cursor-pointer w-full ${
            pullFromListing ? 'bg-[#234012]/5 border-[#234012]' : 'bg-white border-[#e0d9ce] hover:border-[#b6862d]'
          }`}
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
            pullFromListing ? 'bg-[#234012] border-[#234012]' : 'border-[#d0c9be]'
          }`}>
            {pullFromListing && <i className="ri-check-line text-[10px] text-white" />}
          </div>
          <div>
            <p className="text-sm font-medium font-sans text-[#141413]">
              Yes — pull information from my existing website or listing
            </p>
            <p className="text-xs font-sans text-[#77776f] mt-1">
              We&apos;ll use it as a starting point. You&apos;ll approve everything before it goes live.
            </p>
          </div>
        </button>

        {pullFromListing && (
          <div className="flex flex-col gap-1.5 -mt-2">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Paste your listing URL</label>
            <input
              type="url"
              name="listingUrl"
              value={data.listingUrl || ''}
              onChange={(e) => onChange('listingUrl', e.target.value)}
              className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
              placeholder="https://www.airbnb.com/rooms/..."
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Villa Name</label>
          <input
            type="text"
            name="villaName"
            required
            value={data.villaName || ''}
            onChange={(e) => onChange('villaName', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
            placeholder="e.g. Villa Serena"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Country</label>
            <div className="relative">
              <select
                value={countrySelect}
                onChange={(e) => {
                  const val = e.target.value;
                  setCountrySelect(val);
                  if (val !== 'Other') {
                    onChange('country', val);
                  } else {
                    onChange('country', '');
                  }
                }}
                className="w-full bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors appearance-none cursor-pointer"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-[#77776f] pointer-events-none" />
            </div>
            {countrySelect === 'Other' && (
              <input
                type="text"
                name="country"
                required
                autoFocus
                value={data.country || ''}
                onChange={(e) => onChange('country', e.target.value)}
                className="bg-white border border-[#b6862d] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#234012] transition-colors mt-1"
                placeholder="Type your country..."
              />
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Region</label>
            <input
              type="text"
              name="region"
              required
              value={data.region || ''}
              onChange={(e) => onChange('region', e.target.value)}
              className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
              placeholder="e.g. Marbella, Algarve"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Bedrooms</label>
            <input
              type="number"
              name="bedrooms"
              required
              min={1}
              max={30}
              value={data.bedrooms || ''}
              onChange={(e) => onChange('bedrooms', e.target.value)}
              className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
              placeholder="6"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Max Guests</label>
            <input
              type="number"
              name="maxGuests"
              required
              min={1}
              max={50}
              value={data.maxGuests || ''}
              onChange={(e) => onChange('maxGuests', e.target.value)}
              className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
              placeholder="12"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Villa Description
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <p className="text-[#77776f] text-xs font-sans -mt-0.5">
            Already listed on Airbnb, your own site, or anywhere else? Tick the box above and we&apos;ll pull your copy directly — nothing to write here.
          </p>
          <textarea
            name="villaDescription"
            value={data.villaDescription || ''}
            onChange={(e) => onChange('villaDescription', e.target.value)}
            maxLength={500}
            rows={4}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="Or write something here if you prefer — the setting, atmosphere, what makes it special..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.villaDescription || '').length}/500</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Owner Quote
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <textarea
            name="ownerQuote"
            value={data.ownerQuote || ''}
            onChange={(e) => onChange('ownerQuote', e.target.value)}
            maxLength={300}
            rows={3}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="A personal note from you about the villa — what you love most about it..."
          />
          <p className="text-[#77776f] text-xs font-sans">We sometimes feature owner quotes on the listing page.</p>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <button type="button" onClick={onBack} className="text-sm font-sans text-[#77776f] hover:text-[#3a3a34] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="submit"
            className="bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
